import { useState, useCallback } from 'react';
import {
    Button, Table,
} from 'reactstrap';
import { v7 as uuid } from 'uuid';
import { isEmpty } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { SmallButton, NumericInput } from '../common/forms/FormElements';
import { Modal } from '../common/Modal';

export const EquipmentTypePayrollIncentiveGrid = ({
    incentives,
    userGroups,
    onIncentivesChange,
    usStates,
    certifications,
}) => {
    const [editing, setEditing] = useState([]);

    const handleSubmit = useCallback((newIncentive) => {
        const updatedIncentives = [...incentives, newIncentive];
        onIncentivesChange(updatedIncentives);
    }, [incentives, onIncentivesChange]);

    const handleRemoveIncentiveButtonClick = useCallback((id) => {
        const updated = incentives.filter((rule) => rule.id !== id);

        onIncentivesChange(updated);
    }, [incentives, onIncentivesChange]);

    const handleEditIncentiveButtonClick = useCallback((index) => {
        setEditing([...editing, index]);
    }, [editing]);

    const handleUpdateIncentive = useCallback((id, field, value) => {
        const updated = incentives.map((incentive) =>
            incentive.id === id ? { ...incentive, [field]: value } : incentive,
        );
        onIncentivesChange(updated);
    }, [incentives, onIncentivesChange]);

    const handleAddIncentive = useCallback(() => {
        Modal.show({
            title: 'Add Incentive',
            onSubmit: (values) => handleSubmit(values),
            zIndex: 10001,
            initialValues: {
                id: uuid(),
            },
            body: (values) => {
                const incentive = values.values;

                return (
                    <>
                        <NumericInput
                            label="Adjustment"
                            id="amount"
                            name="amount"
                            min={0}
                            step={0.01}
                            error="Amount is required"
                            required
                            startAddon={(
                                <span className="text-muted">$</span>
                            )}
                        />

                        <ValidatedSelect
                            id="certificationId"
                            name="certificationId"
                            label="Certification"
                            options={certifications}
                            isDisabled={!!incentive.applicationUserGroupId || !!incentive.usStateId}
                            isClearable
                        />

                        <ValidatedSelect
                            id="applicationUserGroupId"
                            name="applicationUserGroupId"
                            label="User Group"
                            options={userGroups}
                            isDisabled={!!incentive.certificationId}
                            isClearable
                        />

                        <ValidatedSelect
                            id="usStateId"
                            name="usStateId"
                            label="State/Province Override"
                            options={usStates}
                            isDisabled={!!incentive.certificationId}
                            valueField="id"
                            isClearable
                        />
                    </>
                );
            },
        });
    }, [handleSubmit, userGroups, usStates, certifications]);

    return (
        <div>
            <div className="d-flex justify-content-end mb-1">
                <SmallButton
                    type="button"
                    onClick={handleAddIncentive}
                >
                    <FontAwesomeIcon
                        icon={faPlusCircle}
                        size="md"
                        className="mr-2"
                    />
                    Add Incentive
                </SmallButton>
            </div>

            <Table
                className="table table-sm table-centered"
                bordered
                responsive
            >
                <thead>
                    <tr>
                        <th>Adjustment</th>
                        <th>Certification</th>
                        <th>User Group</th>
                        <th>State</th>
                        <th className="text-center">
                            <FontAwesomeIcon icon={faEdit} />
                        </th>
                        <th className="text-center">
                            <FontAwesomeIcon icon={faTrash} />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {isEmpty(incentives) ? (
                        <tr>
                            <td
                                colSpan={5}
                                className="text-center"
                            >
                                No incentives found.
                            </td>
                        </tr>
                    ) : (
                        incentives.map((incentive) => (
                            <tr key={incentive.id}>
                                {editing.includes(incentive.id) ? (
                                    <td>
                                        <NumericInput
                                            formGroupClassName="mb-0"
                                            name="amount"
                                            required
                                            value={incentive.amount}
                                            size="sm"
                                            startAddon={(
                                                <span className="text-muted">$</span>
                                            )}
                                            onChange={(e) =>
                                                handleUpdateIncentive(incentive.id, 'amount', parseFloat(e.target.value))
                                            }
                                            min={0.01}
                                            step="0.01"
                                        />
                                    </td>
                                ) : (
                                    <td>{incentive.amount.toFixed(2)}</td>
                                )}
                                <td>
                                    {
                                        certifications.find(
                                            (c) => c.value === incentive.certificationId,
                                        )?.label || 'No Certification'
                                    }
                                </td>
                                <td>
                                    {
                                        userGroups.find(
                                            (group) => group.value === incentive.applicationUserGroupId,
                                        )?.label || 'No Group'
                                    }
                                </td>
                                <td>
                                    {
                                        usStates.find(
                                            (state) => state.id === incentive.usStateId,
                                        )?.label || 'No state override'
                                    }
                                </td>
                                <td
                                    className="text-center"
                                    style={{ width: '30px' }}
                                >
                                    <Button
                                        type="button"
                                        color="outline-primary"
                                        size="sm"
                                        onClick={() => handleEditIncentiveButtonClick(incentive.id)}
                                    >
                                        Edit
                                    </Button>
                                </td>
                                <td
                                    className="text-center"
                                    style={{ width: '30px' }}
                                >
                                    <Button
                                        type="button"
                                        color="outline-primary"
                                        onClick={() => handleRemoveIncentiveButtonClick(incentive.id)}
                                        size="sm"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
        </div>
    );
};
